// src/pages/Budget_entering/ImportScreen.jsx
// Экран «Загрузка файла» — основной сценарий наполнения периода из выгрузки
// ИС «Казначейство» (форма 1-27). Период загружается целиком одной транзакцией.
//
// Ошибки разбора/несовпадения региона API возвращает HTTP-статусом (не 200
// с ok:false) — точная форма тела ошибки не подтверждена, поэтому распознаём
// её defensively по наличию полей errors/file_region, иначе — общий тост.

import React, { useState } from 'react';
import { Select, Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import { importFile, ApiError } from './api';
import { fmtSum, fmtResid } from './format';

const { Option } = Select;

export default function ImportScreen({ budgets, defaultBudget, onImported }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || '').toLowerCase().startsWith('kk') ? 'kk' : 'ru';

  const [budget, setBudget] = useState(defaultBudget);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (busy) return; // повторное нажатие не создаёт второй запрос
    if (!file) {
      notification.error({ message: t('budgetEntering.impNoFile'), placement: 'bottomRight' });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await importFile({ budget, file });
      setResult(res);
      if (res.ok) {
        notification.success({
          message: t('budgetEntering.impSaved'),
          placement: 'bottomRight',
          description: (
            <a onClick={(e) => e.preventDefault()} href="#log">
              {t('budgetEntering.toLog')}
            </a>
          ),
        });
        onImported(); // обновить состав строк на экране «Строки отчёта»
      }
    } catch (e) {
      const body = e instanceof ApiError ? e.body : null;
      if (body && Array.isArray(body.errors)) {
        setResult({ kind: 'parse', errors: body.errors });
      } else if (body && body.file_region) {
        setResult({ kind: 'region', file_region: body.file_region, chosen_region: body.chosen_region });
      } else {
        notification.error({ message: t('budgetEntering.toastNet'), placement: 'bottomRight' });
      }
    } finally {
      setBusy(false);
    }
  };

  const bName = (k) => {
    const b = budgets.find((x) => x.k === k) || {};
    return lang === 'kk' ? b.kk : b.ru;
  };

  return (
    <section>
      <div className="bp-card">
        <div className="bp-card-h">
          <h2>{t('budgetEntering.impTitle')}</h2>
        </div>
        <div className="bp-card-b">
          <div className="bp-grid2">
            <div className="bp-field">
              <label>
                {t('budgetEntering.lIBudget')}
                <span className="bp-req">*</span>
              </label>
              <Select value={budget} onChange={setBudget} style={{ width: '100%' }}>
                {budgets.map((b) => (
                  <Option key={b.k} value={b.k}>
                    {lang === 'kk' ? b.kk : b.ru}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="bp-field">
              <label>
                {t('budgetEntering.lIFile')}
                <span className="bp-req">*</span>
              </label>
              <input
                className="bp-inp"
                type="file"
                accept=".xls,.xml"
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
            </div>
          </div>

          {/* Врезку о замене периода показываем всегда — оператор должен знать правило
              до нажатия кнопки. */}

          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={run} loading={busy}>
              {t('budgetEntering.btnImport')}
            </Button>
          </div>

          {result && (
            <div style={{ marginTop: 20 }}>
              <ImportResult result={result} bName={bName} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ImportResult({ result, bName }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || '').toLowerCase().startsWith('kk') ? 'kk' : 'ru';

  if (result.kind === 'region') {
    return (
      <div className="bp-note bad">
        {t('budgetEntering.impRegion', { fr: result.file_region, cr: result.chosen_region })}
      </div>
    );
  }
  if (result.kind === 'parse') {
    // Контракт ошибки не подтверждён для импорта отдельно — переиспользуем
    // { field, code, message, message_ru, message_kk } строк /rows; line
    // показываем, только если бэкенд его всё же пришлёт.
    return (
      <div className="bp-note bad">
        <b>{t('budgetEntering.impParseTitle')}</b>
        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {result.errors.map((e, i) => {
            const m = (lang === 'kk' ? e.message_kk : e.message_ru) || e.message;
            return (
              <li key={i}>
                {e.line !== undefined && e.line !== null
                  ? t('budgetEntering.impLine', { n: e.line, m })
                  : m}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // Успех / успех с нарушением тождеств — врезка + три плитки + семь проверок.
  const viol = result.kind === 'violation';
  return (
    <div>
      <div className={`bp-note ${viol ? 'warn' : 'good'}`}>
        <b>{viol ? t('budgetEntering.impDoneViol') : t('budgetEntering.impDone')}</b>
        <br />
        {t('budgetEntering.impStats', { f: result.file_name, n: result.rows, d: result.date })}
        {viol && (
          <>
            <br />
            {t('budgetEntering.impViolNote')}
          </>
        )}
      </div>
      <div className="bp-kpis" style={{ marginTop: 14, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="bp-kpi">
          <div className="c">{t('budgetEntering.kRes')}</div>
          <div className="v">{fmtSum(result.kpi.resources)}</div>
        </div>
        <div className="bp-kpi">
          <div className="c">{t('budgetEntering.kUse')}</div>
          <div className="v">{fmtSum(result.kpi.uses)}</div>
        </div>
        <div className="bp-kpi">
          <div className="c">{t('budgetEntering.kResid')}</div>
          <div className="v">{fmtResid(result.kpi.residual)}</div>
        </div>
      </div>
      <div className="bp-sectitle" style={{ marginTop: 20 }}>
        {t('budgetEntering.balChecks')}
      </div>
      <div>
        {result.checks.map((c) => (
          <div className={`bp-chk ${c.ok ? 'ok' : 'no'}`} key={c.id}>
            <span className="ic">{c.ok ? '✓' : '✗'}</span>
            <span>{t(`budgetEntering.${c.key}`, c.formula)}</span>
            <span className="res">
              {t('budgetEntering.resid')}: {fmtResid(c.residual)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
